import { Navigate, Route, Routes } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Toaster } from './components/ui/toaster'
import { Layout } from './components/layout/Layout'
import { ProjectsHubPage } from './pages/ProjectsHubPage'
import { SmartIntakePage } from './pages/SmartIntakePage'
import { NewProjectWizardPage } from './pages/new-project/NewProjectWizardPage'
import { CommandCenterPage } from './pages/CommandCenterPage'
import { AuditPage } from './pages/AuditPage'
import { LabPage } from './pages/LabPage'
import { LoginPage } from './pages/LoginPage'
import { KeyboardShortcutsOverlay } from './components/layout/KeyboardShortcutsOverlay'
import { useGlobalShortcuts } from './hooks/useGlobalShortcuts'
import { applyTheme, useThemeStore } from './stores/themeStore'
import { useAuthStore } from './stores/useAuthStore'

function App() {
  // Theme — defaults to dark (UX v0.7 baseline) but the user can flip
  // to light via the Layout's Sun toggle or the ``T`` global shortcut.
  // We sync the persisted store value into the ``<html>`` class on
  // every change so Tailwind's ``dark:`` variants resolve correctly
  // (plan §P2.18).
  const theme = useThemeStore((s) => s.theme)
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // Session auth — pull /api/auth/session once on boot so the Layout's
  // logout button knows whether to render and so the LoginPage knows
  // whether to bounce an already-signed-in user straight to /. When
  // the backend has no credentials configured this comes back with
  // ``auth_enabled: false`` and the SPA renders normally.
  const hydrateAuth = useAuthStore((s) => s.hydrate)
  useEffect(() => {
    void hydrateAuth()
  }, [hydrateAuth])

  // Global keyboard cheat sheet — pressing ``?`` from anywhere outside
  // an editable input opens the overlay (plan §P2.19 / audit §8.14).
  // We pass ``setShortcutsOpen`` into the global-shortcuts hook so
  // every page benefits without each one having to re-wire the keymap.
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  useGlobalShortcuts({ openShortcuts: () => setShortcutsOpen(true) })

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Routes>
        {/* /login is registered OUTSIDE the Layout so the login page
            doesn't render the top bar (which itself has links and a
            logout button that would be nonsensical pre-auth). */}
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={<Layout onOpenShortcuts={() => setShortcutsOpen(true)} />}
        >
          <Route index element={<Navigate to="/projects" replace />} />
          <Route path="projects" element={<ProjectsHubPage />} />
          <Route path="intake" element={<SmartIntakePage />} />
          <Route path="intake/v2" element={<NewProjectWizardPage />} />
          <Route path="projects/:projectId" element={<CommandCenterPage />} />
          <Route
            path="projects/:projectId/runs/:runId"
            element={<CommandCenterPage />}
          />
          <Route
            path="projects/:projectId/legacy"
            element={<CommandCenterPage legacyBriefing />}
          />
          <Route
            path="projects/:projectId/runs/:runId/legacy"
            element={<CommandCenterPage legacyBriefing />}
          />
          {/* T7 — audit viewer. Mounted unconditionally; the backend
              ``/api/projects/{id}/audit/timeline`` endpoint already
              gates on the producer-side ``ai_alm_test_audit_writer``
              flag, so a deployment with the writer OFF surfaces an
              empty timeline rather than a route 404. */}
          <Route
            path="projects/:projectId/audit"
            element={<AuditPage />}
          />
          {/* T10 — Test Lab. Mounted unconditionally; the backend's
              ``/api/test-runs`` endpoint 404s when
              ``AI_ALM_TEST_LAB_ENABLED`` is OFF and the SPA renders a
              "Test Lab is not enabled" notice rather than an error
              toast. */}
          <Route path="lab" element={<LabPage />} />
          {/* Legacy per-project intake path redirects to the new single flow. */}
          <Route
            path="projects/:projectId/intake"
            element={<Navigate to="/intake" replace />}
          />
        </Route>
      </Routes>
      <Toaster />
      <KeyboardShortcutsOverlay
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
    </div>
  )
}

export default App
